import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Paper, Button, Typography, Alert, Grid, CircularProgress } from '@mui/material';
import Logo from './UI/AppBars/Logo/Logo';
import Copyright from './UI/Copyright/Copyright';
import { useTranslation } from 'react-i18next';

/**
 * OAuth consent screen for MCP clients (Claude Code, Claude Desktop, …).
 *
 * Evora's authorization server redirects here with a signed, short-lived
 * `request` token once it has validated the client and redirect_uri. This
 * component implements no authentication of its own: with no session it hands
 * off to the normal Login screen, so DB, Google and Entra all keep working,
 * tenant picker included.
 *
 * On approval it POSTs the user's Evora token to /oauth/authorize/complete,
 * which mints a single-use authorization code and returns the loopback URL to
 * navigate to. The access token itself never travels through a URL.
 *
 * Layout deliberately mirrors Portal/PortalAccept.jsx — the other "approve this
 * access" screen — so the two read as the same product.
 *
 * See MCP/docs/OAUTH-BROWSER-LOGIN.md.
 */
export default function OAuthAuthorize() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [imageNumber] = useState(() => Math.floor(Math.random() * 3) + 1);

    const params = new URLSearchParams(location.search);
    const requestToken = params.get('request');

    const [client, setClient] = useState(null);
    const [scopes, setScopes] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const authToken = () => sessionStorage.getItem('jwtToken') || localStorage.getItem('jwtToken');

    const fail = useCallback((message) => {
        setError(message);
        setLoading(false);
        setSubmitting(false);
    }, []);

    useEffect(() => {
        let cancelled = false;

        if (!requestToken) {
            fail(t('oauthMissingRequest'));
            return;
        }
        if (!authToken()) {
            // Send the user through the normal login, then return here. Mirrors
            // the existing partnerInviteRedirect idiom in PortalAccept.
            sessionStorage.setItem('oauthAuthorizeRedirect', `/oauth-authorize${location.search}`);
            navigate('/login');
            return;
        }

        axios.get('/oauth/authorize/info', { params: { request: requestToken } })
            .then(res => {
                if (cancelled) return;
                // A misconfigured dev proxy returns index.html with a 200, which
                // otherwise renders as a nameless request. Treat it as an error.
                if (!res.data || typeof res.data !== 'object' || !res.data.clientName) {
                    fail(t('oauthRequestExpired'));
                    return;
                }
                setClient(res.data.clientName);
                setScopes(res.data.scopes || []);
                setLoading(false);
            })
            .catch(err => {
                if (cancelled) return;
                fail(err.response?.data?.error_description || t('oauthRequestExpired'));
            });

        return () => { cancelled = true; };
    }, [requestToken, location.search, navigate, fail, t]);

    const approve = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const token = String(authToken() || '').replace(/^Bearer\s+/i, '');
            const res = await axios.post('/oauth/authorize/complete',
                { requestToken },
                { headers: { Authorization: 'Bearer ' + token } });
            if (!res.data?.redirect) {
                fail(t('oauthAuthorizeFailed'));
                return;
            }
            // Leave the SPA entirely — the target is the client's loopback URI.
            window.location = res.data.redirect;
        } catch (err) {
            fail(err.response?.data?.error_description || t('oauthAuthorizeFailed'));
        }
    };

    const content = () => {
        if (error) return <Alert severity="error">{error}</Alert>;
        if (loading) return <CircularProgress />;
        return (
            <>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 600, mb: 2 }}>
                    {t('oauthAuthorizeTitle')}
                </Typography>
                <Typography sx={{ mb: 2 }}>
                    {/*
                      * The client name is rendered here rather than interpolated,
                      * so it can be emphasised. Passing an empty `client` value
                      * collapses the {{client}} placeholder in older cached
                      * copies of this string — locales are require()d by the API
                      * and survive until it restarts, so both shapes must render
                      * correctly.
                      */}
                    <strong>{client}</strong> {t('oauthAuthorizePrompt', { client: '' }).trim()}
                </Typography>

                {scopes.length > 0 &&
                    <Box component="ul" sx={{ mb: 3, pl: 3 }}>
                        {scopes.map(s =>
                            <li key={s}>
                                <Typography variant="body2">
                                    {t('oauthScope_' + s.replace(':', '_'), s)}
                                </Typography>
                            </li>)}
                    </Box>}

                <Button variant="contained" size="large" fullWidth
                        disabled={submitting} onClick={approve} sx={{ mb: 1.5 }}>
                    {t('oauthApprove')}
                </Button>
                <Button variant="outlined" size="large" fullWidth
                        disabled={submitting} onClick={() => navigate('/home')}>
                    {t('oauthDeny')}
                </Button>
            </>
        );
    };

    return (
        <Grid container component="main" sx={{ height: '100vh' }}>
            <Grid
                sx={{
                    backgroundImage: `url(/EvoraLogin-${imageNumber}.jpg)`,
                    backgroundRepeat: 'no-repeat',
                    backgroundColor: 'grey[50]',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
                size={{ xs: false, sm: 4, md: 7 }}
            />
            <Grid component={Paper} elevation={6} square size={{ xs: 12, sm: 8, md: 5 }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    px: 4,
                    pt: 6,
                }}>
                    <Logo height={"100%"} width={"100%"} noTenant />
                    <Box sx={{ mt: 4, width: '100%', maxWidth: 400 }}>
                        {content()}
                    </Box>
                    <Box mt={5}>
                        <Copyright />
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
}
